import { apiGetJSON } from "../client.js";
import { cancelRequest, composeAbortSignal, isAbortError, startRequest } from "../core/request-coordinator.js";
import { normalizeAdminConsoleReadModel } from "../domain/readmodels.js";
import { routeNeedsCollectionPolling, threadNeedsCollectionPolling } from "./polling.js";
import {
  cancelThreadComponentRequests,
  createThreadComponentState,
  loadThreadComponent,
  refreshThreadComponent,
  startThreadComponentLive,
  stopThreadComponentLive
} from "../thread-component.js";
import {
  cancelTurnDetailRequests,
  loadTurnDetail,
  refreshTurnDetail,
  resetTurnDetailState,
  startTurnDetailLive,
  stopTurnDetailLive
} from "../turn-detail-component.js";
import { cancelWorkspaceContentRequests } from "../workspace-content-component.js";

export { routeNeedsCollectionPolling, threadNeedsCollectionPolling } from "./polling.js";

export async function handleAdminRouteChange(state, route, options = {}) {
  const wasThreadDetail = state.route.id === "thread-detail";
  const isThreadDetail = route.id === "thread-detail";
  if (!wasThreadDetail && isThreadDetail) {
    state.navCollapsedBeforeThread = state.navCollapsed;
    state.navCollapsed = true;
  } else if (wasThreadDetail && !isThreadDetail) {
    state.navCollapsed = false;
    state.navCollapsedBeforeThread = null;
  }
  state.route = route;
  state.loading = true;
  state.settingsOpen = false;
  state.detailDrawerOpen = false;
  state.promotionConfirm = null;
  if (route.id !== "workspaces" && route.id !== "thread-detail") {
    options.resetWorkspaceContentPanel?.();
  }
  state.threadContentPanelOpen = false;
  stopAdminLiveResources(state);
  cancelThreadComponentRequests(state.threadDetail);
  cancelTurnDetailRequests(state.turnDetail);
  cancelWorkspaceContentRequests(state.workspaceContent);
  cancelRequest(state.requests.collections);
  options.render?.();

  const routeRequest = startRequest(state.requests.route);
  try {
    const loaded = await loadAdminBaseData(state, {
      request: routeRequest,
      signal: routeRequest.signal
    }, options);
    if (!loaded) {
      return;
    }
    if (route.id === "thread-detail") {
      const loadedThread = await loadThreadComponent(state.threadDetail, state.apiKey, route.params.threadID, {
        request: routeRequest,
        signal: routeRequest.signal
      });
      if (!loadedThread) {
        return;
      }
      resetAdminTurnDetail(state);
    } else if (route.id === "turn-detail") {
      const loadedTurn = await loadTurnDetail(state.turnDetail, state.apiKey, route.params.turnID, {
        ...options.adminTurnDetailOptions?.(),
        request: routeRequest,
        signal: routeRequest.signal
      });
      if (!loadedTurn) {
        return;
      }
      resetAdminThreadDetail(state);
    } else {
      resetAdminThreadDetail(state);
      resetAdminTurnDetail(state);
    }
    if (!routeRequest.isCurrent()) {
      return;
    }
    state.loading = false;
    options.render?.();
    startAdminLiveResources(state, route, options);
  } catch (error) {
    if (isAbortError(error) || !routeRequest.isCurrent()) {
      return;
    }
    if (error?.status === 401 && typeof options.handleAuthFailure === "function") {
      options.handleAuthFailure(error);
      return;
    }
    state.loading = false;
    options.setStatus?.(error.message, "error");
    options.render?.();
  }
}

export async function loadAdminBaseData(state, loadOptions = {}, options = {}) {
  const request = loadOptions.request || startRequest(state.requests.collections);
  const signal = composeAbortSignal(request.signal, loadOptions.signal);
  const snapshot = await fetchAdminBaseDataSnapshot(state, { signal });
  if (!request.isCurrent()) {
    return false;
  }
  applyAdminBaseDataSnapshot(state, snapshot, options);
  return true;
}

export async function refreshAdminCollections(state, options = {}) {
  if (!state.apiKey) {
    return false;
  }

  const request = startRequest(state.requests.collections);
  const signal = composeAbortSignal(request.signal);
  try {
    const snapshot = normalizeAdminConsoleReadModel(await apiGetJSON(state.apiKey, "/internal/readmodels/admin-console", { signal }));
    if (!request.isCurrent()) {
      return false;
    }

    const nextTurns = snapshot.turns;
    const nextThreads = snapshot.threads;
    const nextPromotions = snapshot.promotions;
    const nextPendingDecisions = snapshot.pendingDecisions;
    const listChanged = !options.sameTurnSummary?.(state.turns, nextTurns) ||
      !options.sameThreadSummary?.(state.threads, nextThreads) ||
      !options.samePromotionSummary?.(state.promotions, nextPromotions) ||
      !options.sameDecisionSummary?.(state.pendingDecisions, nextPendingDecisions);

    state.turns = nextTurns;
    state.threads = nextThreads;
    state.promotions = nextPromotions;
    state.pendingDecisions = nextPendingDecisions;

    if (!routeNeedsCollectionPolling(state, state.route, options) && state.live.pollHandle) {
      window.clearInterval(state.live.pollHandle);
      state.live.pollHandle = null;
    }

    let detailChanged = false;
    if (state.route.id === "thread-detail" && state.route.params.threadID) {
      detailChanged = (await refreshThreadComponent(state.threadDetail, state.apiKey, state.route.params.threadID, {
        ...options.adminThreadComponentOptions?.(),
        request,
        signal
      })) || detailChanged;
    }
    if (state.route.id === "turn-detail" && state.route.params.turnID) {
      detailChanged = (await refreshTurnDetail(state.turnDetail, state.apiKey, state.route.params.turnID, {
        ...options.adminTurnDetailOptions?.(),
        includeEvents: false,
        request,
        signal
      })) || detailChanged;
    }
    if (!request.isCurrent()) {
      return false;
    }
    if (listChanged || detailChanged) {
      options.render?.();
    }
    return true;
  } catch (error) {
    if (isAbortError(error) || !request.isCurrent()) {
      return false;
    }
    options.setStatus?.(error.message, "error");
    options.render?.();
    return false;
  }
}

export function startAdminLiveResources(state, route, options = {}) {
  if (!state.apiKey) {
    return;
  }
  if (routeNeedsCollectionPolling(state, route, options)) {
    state.live.pollHandle = window.setInterval(() => {
      void refreshAdminCollections(state, options);
    }, 6000);
  }
  if (route.id === "thread-detail" && route.params.threadID) {
    startThreadComponentLive(state.threadDetail, state.apiKey, route.params.threadID, options.adminThreadComponentOptions?.());
  }
  if (route.id === "turn-detail" && route.params.turnID) {
    startTurnDetailLive(state.turnDetail, state.apiKey, route.params.turnID, options.adminTurnDetailOptions?.());
  }
}

export function stopAdminLiveResources(state) {
  stopThreadComponentLive(state.threadDetail);
  stopTurnDetailLive(state.turnDetail);
  if (state.live.pollHandle) {
    window.clearInterval(state.live.pollHandle);
    state.live.pollHandle = null;
  }
}

export function resetAdminTurnDetail(state) {
  resetTurnDetailState(state.turnDetail);
}

export function resetAdminThreadDetail(state) {
  stopThreadComponentLive(state.threadDetail);
  cancelThreadComponentRequests(state.threadDetail);
  state.threadDetail = createThreadComponentState();
}

async function fetchAdminBaseDataSnapshot(state, options = {}) {
  if (!state.apiKey) {
    return {
      agents: [],
      accounts: [],
      harnesses: [],
      workspaces: [],
      threads: [],
      turns: [],
      promotions: [],
      pendingDecisions: [],
      inferenceEnabled: false,
      inferenceCredentials: [],
      inferenceRoutes: [],
      inferenceTraffic: [],
      clearDetails: true
    };
  }

  const [snapshot, accounts] = await Promise.all([
    apiGetJSON(state.apiKey, "/internal/readmodels/admin-console", { signal: options.signal }),
    loadAccountSelectorItems(state, options)
  ]);
  return {
    ...normalizeAdminConsoleReadModel(snapshot),
    accounts,
    clearDetails: false
  };
}

function applyAdminBaseDataSnapshot(state, snapshot, options = {}) {
  state.accounts = snapshot.accounts || [];
  state.agents = snapshot.agents;
  state.harnesses = snapshot.harnesses;
  state.workspaces = snapshot.workspaces;
  state.threads = snapshot.threads;
  state.turns = snapshot.turns;
  state.promotions = snapshot.promotions;
  state.pendingDecisions = snapshot.pendingDecisions;
  state.inferenceEnabled = Boolean(snapshot.inferenceEnabled);
  state.inferenceCredentials = snapshot.inferenceCredentials || [];
  state.inferenceRoutes = snapshot.inferenceRoutes || [];
  state.inferenceTraffic = snapshot.inferenceTraffic || [];

  if (state.editingAgentID) {
    const selectedAgent = options.lookupByID?.(state.agents, state.editingAgentID);
    if (!selectedAgent) {
      state.editingAgentID = "";
      state.forms.agent = options.defaultAgentDraft?.() || state.forms.agent;
    } else if (options.agentDraftFromItem) {
      state.forms.agent = options.agentDraftFromItem(selectedAgent);
    }
  }

  if (state.editingWorkspaceID) {
    const selectedWorkspace = options.lookupByID?.(state.workspaces, state.editingWorkspaceID);
    if (!selectedWorkspace) {
      state.editingWorkspaceID = "";
      state.forms.workspace = options.defaultWorkspaceDraft?.() || state.forms.workspace;
    } else if (options.workspaceDraftFromItem) {
      state.forms.workspace = options.workspaceDraftFromItem(selectedWorkspace);
    }
  }

  if (
    state.workspaceContent.workspaceID &&
    options.lookupByID &&
    !options.lookupByID(state.workspaces, state.workspaceContent.workspaceID)
  ) {
    options.resetWorkspaceContentPanel?.();
  }

  if (snapshot.clearDetails) {
    resetAdminThreadDetail(state);
    resetAdminTurnDetail(state);
    state.editingAgentID = "";
    state.editingWorkspaceID = "";
  }

  options.syncAgentDraftDefaults?.();
}

async function loadAccountSelectorItems(state, options = {}) {
  try {
    const response = await apiGetJSON(state.apiKey, "/v1/accounts?limit=200", { signal: options.signal });
    return Array.isArray(response?.items) ? response.items : [];
  } catch (error) {
    if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
      return [];
    }
    throw error;
  }
}
